// src/components/auth/ProtectedRoute.jsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { 
  isAuthenticated, 
  isUserActive,
  hasPermission, 
  hasAnyPermission, 
  hasAllPermissions,
  hasRole,
  hasAnyRole,
  isAdmin,
  isDirector,
  isManager,
  isFieldWorker,
  canAccessWeb
} from './authUtils';

/**
 * ProtectedRoute Component
 * 
 * Protects routes with authentication, permission, and role checks.
 * Redirects to login if not authenticated, or shows access denied if lacking permissions.
 * 
 * @param {React.ReactNode} children - Components to render if access granted
 * @param {string} permission - Required permission
 * @param {string[]} anyPermissions - Any of these permissions required
 * @param {string[]} allPermissions - All of these permissions required
 * @param {string} role - Required role
 * @param {string[]} anyRoles - Any of these roles required
 * @param {boolean} requireAuth - Require authentication (default: true)
 * @param {boolean} requireWebAccess - Require web access flag (default: true)
 * @param {boolean} adminOnly - Require Administrator role
 * @param {boolean} directorOnly - Require Director role
 * @param {boolean} managerOnly - Require Manager role
 * @param {boolean} fieldWorkerOnly - Require field worker role
 * @param {string} redirectTo - Redirect path when not authenticated (default: /login)
 * @param {React.ReactNode} fallback - Component to show when access denied (optional)
 * 
 * @example
 * // Require authentication only
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <Dashboard />
 *   </ProtectedRoute>
 * } />
 * 
 * @example
 * // Require specific permission
 * <Route path="/users" element={
 *   <ProtectedRoute permission={PERMISSIONS.USER_VIEW}>
 *     <UserList />
 *   </ProtectedRoute>
 * } />
 * 
 * @example
 * // Require any of multiple permissions
 * <Route path="/projects" element={
 *   <ProtectedRoute anyPermissions={[PERMISSIONS.PROJECT_VIEW, PERMISSIONS.PROJECT_CREATE]}>
 *     <ProjectList />
 *   </ProtectedRoute>
 * } />
 * 
 * @example
 * // Require specific role
 * <Route path="/admin" element={
 *   <ProtectedRoute role={USER_ROLES.ADMINISTRATOR}>
 *     <AdminPanel />
 *   </ProtectedRoute>
 * } />
 * 
 * @example
 * // Admin only shortcut
 * <Route path="/system" element={
 *   <ProtectedRoute adminOnly={true}>
 *     <SystemSettings />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute = ({
  children,
  permission,
  anyPermissions,
  allPermissions,
  role,
  anyRoles,
  requireAuth = true,
  requireWebAccess = true,
  adminOnly = false,
  directorOnly = false,
  managerOnly = false,
  fieldWorkerOnly = false,
  redirectTo = '/login',
  fallback,
}) => {
  const location = useLocation();

  // Check authentication
  if (requireAuth && !isAuthenticated()) {
    // Redirect to login, preserving the intended destination
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check if user is active
  if (requireAuth && !isUserActive()) {
    return <AccessDenied reason="Your account is not active. Please contact administrator." />;
  }

  // Check web access flag
  if (requireWebAccess && !canAccessWeb()) {
    return <AccessDenied reason="You don't have web access. This account is configured for mobile access only." />;
  }

  // Check role-based access (shortcuts)
  if (adminOnly && !isAdmin()) {
    return fallback || <AccessDenied reason="Administrator access required." />;
  }

  if (directorOnly && !isDirector()) {
    return fallback || <AccessDenied reason="Director access required." />;
  }

  if (managerOnly && !isManager()) {
    return fallback || <AccessDenied reason="Manager access required." />;
  }

  if (fieldWorkerOnly && !isFieldWorker()) {
    return fallback || <AccessDenied reason="Field worker access required." />;
  }

  // Check specific role
  if (role && !hasRole(role)) {
    return fallback || <AccessDenied reason={`Role '${role}' required.`} />;
  }

  // Check any of multiple roles
  if (anyRoles && anyRoles.length > 0 && !hasAnyRole(anyRoles)) {
    return fallback || <AccessDenied reason="You don't have the required role to access this page." />;
  }

  // Check specific permission
  if (permission && !hasPermission(permission)) {
    return fallback || <AccessDenied reason="You don't have permission to access this page." />;
  }

  // Check any of multiple permissions
  if (anyPermissions && anyPermissions.length > 0 && !hasAnyPermission(anyPermissions)) {
    return fallback || <AccessDenied reason="You don't have the required permissions to access this page." />;
  }

  // Check all of multiple permissions
  if (allPermissions && allPermissions.length > 0 && !hasAllPermissions(allPermissions)) {
    return fallback || <AccessDenied reason="You don't have all the required permissions to access this page." />;
  }

  // All checks passed - render children
  return <>{children}</>;
};

/**
 * AccessDenied Component
 * 
 * Default component shown when access is denied.
 * Can be customized via fallback prop in ProtectedRoute.
 * 
 * @param {string} reason - Reason for access denial
 */
const AccessDenied = ({ reason = "You don't have permission to access this page." }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h2>

          {/* Reason */}
          <p className="text-gray-600 mb-6">
            {reason}
          </p>

          {/* Actions */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>

          {/* Help text */}
          <p className="text-sm text-gray-500 mt-6">
            If you believe you should have access, please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * PublicRoute Component
 * 
 * Routes that should only be accessible when NOT authenticated.
 * Useful for login, register pages - redirects to dashboard if already logged in.
 * 
 * @param {React.ReactNode} children - Components to render
 * @param {string} redirectTo - Where to redirect if authenticated (default: /dashboard)
 * 
 * @example
 * <Route path="/login" element={
 *   <PublicRoute>
 *     <LoginPage />
 *   </PublicRoute>
 * } />
 */
export const PublicRoute = ({ children, redirectTo = '/dashboard' }) => {
  if (isAuthenticated()) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

/**
 * AdminRoute Component
 * 
 * Shortcut for routes that require Administrator role.
 * 
 * @example
 * <Route path="/admin" element={
 *   <AdminRoute>
 *     <AdminPanel />
 *   </AdminRoute>
 * } />
 */
export const AdminRoute = ({ children, fallback }) => {
  return (
    <ProtectedRoute adminOnly={true} fallback={fallback}>
      {children}
    </ProtectedRoute>
  );
};

/**
 * DirectorRoute Component
 * 
 * Shortcut for routes that require Director role.
 * 
 * @example
 * <Route path="/executive" element={
 *   <DirectorRoute>
 *     <ExecutiveDashboard />
 *   </DirectorRoute>
 * } />
 */
export const DirectorRoute = ({ children, fallback }) => {
  return (
    <ProtectedRoute directorOnly={true} fallback={fallback}>
      {children}
    </ProtectedRoute>
  );
};

/**
 * ManagerRoute Component
 * 
 * Shortcut for routes that require Manager role.
 * 
 * @example
 * <Route path="/manager" element={
 *   <ManagerRoute>
 *     <ManagerDashboard />
 *   </ManagerRoute>
 * } />
 */
export const ManagerRoute = ({ children, fallback }) => {
  return (
    <ProtectedRoute managerOnly={true} fallback={fallback}>
      {children}
    </ProtectedRoute>
  );
};

/**
 * FieldWorkerRoute Component
 * 
 * Shortcut for routes that require field worker role.
 * Note: Field workers typically use mobile app, but this protects web routes if needed.
 * 
 * @example
 * <Route path="/field" element={
 *   <FieldWorkerRoute>
 *     <FieldWorkerPortal />
 *   </FieldWorkerRoute>
 * } />
 */
export const FieldWorkerRoute = ({ children, fallback }) => {
  return (
    <ProtectedRoute fieldWorkerOnly={true} fallback={fallback}>
      {children}
    </ProtectedRoute>
  );
};

export default ProtectedRoute;